// ============================================
// Command Center - Main Dashboard with 4 Queues
// ============================================

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Users,
  CheckCircle,
  XCircle,
  ArrowLeft,
  MessageSquare,
  RefreshCw,
  Phone
} from 'lucide-react';
import { useUIStore, useToast } from '../stores';
import { leadsApi, dashboardApi, type Lead, type DashboardStats } from '../lib/api';
import { PageLoader, EmptyState, ProjectBadge, ErrorState, StageBadge } from '../components';
import { cn, formatRelativeTime, formatCurrency, truncate } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

export function CommandCenter() {
  const navigate = useNavigate();
  const activeProject = useUIStore((state) => state.activeProject);
  const toast = useToast();
  const { t } = useTranslation();

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats', activeProject],
    queryFn: () => dashboardApi.stats(activeProject === 'all' ? undefined : activeProject),
    refetchInterval: 30000,
  });

  const fetchLeadsByStage = async (stage: string) => {
    const projectId = activeProject === 'all' ? undefined : activeProject;
    const result = await leadsApi.list({ stage, project_id: projectId, limit: '50' });
    return result.leads;
  };

  const { data: newLeads = [], isLoading: newLoading } = useQuery({
    queryKey: ['leads', 'new', activeProject],
    queryFn: () => fetchLeadsByStage('new'),
    refetchInterval: 15000,
  });

  const { data: waitingLeads = [], isLoading: waitingLoading } = useQuery({
    queryKey: ['leads', 'waiting_eligibility', activeProject],
    queryFn: () => fetchLeadsByStage('waiting_eligibility'),
    refetchInterval: 15000,
  });

  const { data: approvedLeads = [], isLoading: approvedLoading } = useQuery({
    queryKey: ['leads', 'approved', activeProject],
    queryFn: () => fetchLeadsByStage('approved'),
    refetchInterval: 15000,
  });

  const { data: rejectedLeads = [], isLoading: rejectedLoading } = useQuery({
    queryKey: ['leads', 'rejected', activeProject],
    queryFn: () => fetchLeadsByStage('rejected'),
    refetchInterval: 30000,
  });

  const handleLeadClick = (leadId: string) => navigate(`/leads/${leadId}`);
  const handleRefresh = () => { refetchStats(); toast.info(t('commandCenter.refreshing')); };

  if (statsLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('commandCenter.title')}</h1>
          <p className="text-slate-400 mt-1">{t('app.subtitle')}</p>
        </div>
        <button onClick={handleRefresh} className="btn-secondary">
          <RefreshCw className="w-4 h-4" />
          {t('commandCenter.refresh')}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<Users className="w-5 h-5" />} label={t('commandCenter.leadsToday')} value={stats?.today?.newLeads || 0} color="blue" />
        <StatCard icon={<MessageSquare className="w-5 h-5" />} label={t('commandCenter.responseRate')} value={`${Math.round((stats?.conversionRates?.eligibility_approval_rate || 0) * 100)}%`} color="cyan" />
        <StatCard icon={<CheckCircle className="w-5 h-5" />} label={t('commandCenter.messagesUnread')} value={stats?.today?.messages || 0} color="green" />
        <StatCard icon={<Clock className="w-5 h-5" />} label={t('commandCenter.revenueMonth')} value={stats?.pendingAlerts || 0} color="amber" alert={stats?.pendingAlerts ? stats.pendingAlerts > 0 : false} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <QueueColumn title={t('commandCenter.newLeads')} icon={<Users className="w-5 h-5 text-blue-400" />} count={newLeads.length} color="blue" leads={newLeads} loading={newLoading} onLeadClick={handleLeadClick} emptyTitle={t('commandCenter.noLeads')} emptyDesc={t('commandCenter.noLeadsDesc')} />
        <QueueColumn title={t('commandCenter.waitingEligibility')} icon={<Clock className="w-5 h-5 text-amber-400" />} count={waitingLeads.length} color="amber" leads={waitingLeads} loading={waitingLoading} onLeadClick={handleLeadClick} emptyTitle={t('commandCenter.noLeads')} emptyDesc={t('commandCenter.noLeadsDesc')} />
        <QueueColumn title={t('commandCenter.approvedNotEntered')} icon={<CheckCircle className="w-5 h-5 text-green-400" />} count={approvedLeads.length} color="green" leads={approvedLeads} loading={approvedLoading} onLeadClick={handleLeadClick} emptyTitle={t('commandCenter.noLeads')} emptyDesc={t('commandCenter.noLeadsDesc')} />
        <QueueColumn title={t('stages.REJECTED')} icon={<XCircle className="w-5 h-5 text-red-400" />} count={rejectedLeads.length} color="red" leads={rejectedLeads} loading={rejectedLoading} onLeadClick={handleLeadClick} emptyTitle={t('commandCenter.noLeads')} emptyDesc={t('commandCenter.noLeadsDesc')} />
      </div>
    </div>
  );
}

interface StatCardProps { icon: React.ReactNode; label: string; value: string | number; color: 'blue' | 'cyan' | 'green' | 'amber' | 'red'; alert?: boolean; }

function StatCard({ icon, label, value, color, alert }: StatCardProps) {
  const colorClasses = { blue: 'text-blue-400 bg-blue-500/10', cyan: 'text-cyan-400 bg-cyan-500/10', green: 'text-green-400 bg-green-500/10', amber: 'text-amber-400 bg-amber-500/10', red: 'text-red-400 bg-red-500/10' };
  return (
    <div className={cn('stat-card', alert && 'border-amber-500/50 animate-pulse-slow')}>
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded-lg', colorClasses[color])}>{icon}</div>
        <div><p className="stat-value">{value}</p><p className="stat-label">{label}</p></div>
      </div>
    </div>
  );
}

interface QueueColumnProps { title: string; icon: React.ReactNode; count: number; color: 'blue' | 'amber' | 'green' | 'red'; leads: Lead[]; loading: boolean; onLeadClick: (id: string) => void; emptyTitle: string; emptyDesc: string; }

function QueueColumn({ title, icon, count, color, leads, loading, onLeadClick, emptyTitle, emptyDesc }: QueueColumnProps) {
  const borderColors = { blue: 'border-t-blue-500', amber: 'border-t-amber-500', green: 'border-t-green-500', red: 'border-t-red-500' };
  const countColors = { blue: 'bg-blue-500/20 text-blue-400', amber: 'bg-amber-500/20 text-amber-400', green: 'bg-green-500/20 text-green-400', red: 'bg-red-500/20 text-red-400' };
  return (
    <div className={cn('queue-column border-t-2', borderColors[color])}>
      <div className="queue-header">
        <div className="flex items-center gap-2">{icon}<span className="font-medium text-white">{title}</span></div>
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', countColors[color])}>{count}</span>
      </div>
      <div className="queue-body">
        {loading ? <div className="flex items-center justify-center py-8"><div className="spinner w-6 h-6" /></div>
        : leads.length === 0 ? <EmptyState title={emptyTitle} description={emptyDesc} />
        : leads.map((lead) => <LeadCard key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} />)}
      </div>
    </div>
  );
}

interface LeadCardProps { lead: Lead; onClick: () => void; }

function LeadCard({ lead, onClick }: LeadCardProps) {
  const { t } = useTranslation();
  const name = lead.contact ? `${lead.contact.name_first} ${lead.contact.name_last}`.trim() || t('common.noData') : t('common.noData');
  return (
    <div onClick={onClick} className="queue-card group">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-white truncate">{name}</p>
          {lead.product && <p className="text-sm text-slate-400 truncate mt-0.5">{truncate(lead.product.title, 30)}</p>}
        </div>
        <ProjectBadge projectId={lead.project_id} />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-2xs text-slate-500">{formatRelativeTime(lead.created_at)}</span>
        <ArrowLeft className="w-4 h-4 text-slate-600 group-hover:text-primary-400 transition-colors rtl-flip" />
      </div>
      {lead.entered_in_hot && <div className="mt-2 pt-2 border-t border-slate-800"><span className="text-2xs text-green-400"> {t('commandCenter.enteredInHot')}</span></div>}
    </div>
  );
}
